import ImageUploadPattern from 'assets/images/ImageUploadPattern.png';

import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { uploadMedia } from 'store/media/mediaAsync';
import { mediaActions } from 'store/media/mediaSlice';
import { selectQuiz } from 'store/quiz/quizzesSelectors';
import MediaFormats from 'types/MediaFormats';
// Layouts
import { Dialog, DialogTitle, DialogContent, DialogActions } from 'layouts/Dialog';
// Components
import { Button } from 'components/Buttons';
import { Loader } from 'components/Utilities';
import { Checkbox, Select } from 'components/Controls';
import AvatarEditor from 'react-avatar-editor';
// Utilities
import { textFromCamelToNormalCase } from 'utilities/utilities';

const pica = require('pica')();

type Props = {
  open: boolean;
  onClose: () => void;
  initialFormat: MediaFormats;
}

const UploadImageDialog:React.FC<Props> = ({ open, onClose, initialFormat }) => {
  const { t } = useTranslation('common');
  const dispatch = useAppDispatch();

  const editorRef = useRef<any>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const quiz = useAppSelector(selectQuiz);

  const [ isLoading, setIsLoading ] = useState(false);
  const [ fileDataUrl, setFileDataUrl ] = useState<string | null>(null);
  const [ uuid, setUUID ] = useState<string>('');
  const [ isGif, setIsGif ] = useState(checkIsGif(fileDataUrl));

  useEffect(() => {
    setIsGif(checkIsGif(fileDataUrl));
  }, [fileDataUrl]);

  // Upload to account media
  const [ uploadToAccount, setUploadToAccount ] = useState<boolean>(false);
  const handleChangeUploadToAccount = (e:ChangeEvent<HTMLInputElement>) => {
    setUploadToAccount(e.target.checked);
  }

  // Media format
  const [ format, setFormat ] = useState<MediaFormats>(initialFormat);
  const handleFormat = (e:ChangeEvent<HTMLSelectElement>) => {
    setFormat(e.target.value as MediaFormats);
  }

  // Scale
  const [ scale, setScale ] = useState(1);
  const handleChangeScale = (event:ChangeEvent<HTMLInputElement>) => {
    setScale(Number(event.target.value));
  }

  const handleChange = (event:ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    
    if ( !files || !files[0] ) {
      onClose();
      return;
    }
    
    const file = files[0];
    const fileReader:FileReader = new FileReader();
    fileReader.onload = (event:ProgressEvent<FileReader>) => {
      if ( !event.target ) return;
      setFileDataUrl(event.target.result as string);
    }
    fileReader.readAsDataURL(file);
  }

  const handleClickApply = async () => {
    if ( !fileDataUrl ) return;

    setIsLoading(true);

    if ( isGif ){
      uploadGifImage(fileDataUrl);
    } else {
      if ( !editorRef.current ){
        setIsLoading(false);
        throw Error('Avatar editor error');
      }

      const img = editorRef.current.getImage()

      const offScreenCanvas = document.createElement("canvas");

      offScreenCanvas.width = mediasInfo[format].width * 2;
      offScreenCanvas.height = mediasInfo[format].height * 2;

      const picaCanvas = await pica.resize(img, offScreenCanvas);
      picaCanvas.toBlob(
        (blob:Blob) => URL.createObjectURL(blob),
        "image/jpeg",
        0.9
      )

      picaCanvas.toBlob((blob:Blob) => {
        uploadOtherImage(blob);
      });
    }
  }

  const uploadGifImage = async (fileBase64:string) => {
    try {
      const response = await fetch(fileBase64);
      const blob = await response.blob();
      const data:any = { blob, type: 'image', format };
      if (!uploadToAccount) {
        data['quizId'] = quiz?._id;
      }
      const media = await dispatch(uploadMedia(data)).unwrap();
      dispatch(mediaActions.setSelectedMedia(media));
      onClose();
    } catch(error){} finally {
      setIsLoading(false);
    }
  }

  const uploadOtherImage = async (blob:Blob) => {
    try {
      const data:any = { blob, type: 'image', format };
      if (!uploadToAccount && quiz) {
        data['quizId'] = quiz?._id;
      }
      const media = await dispatch(uploadMedia(data)).unwrap();
      dispatch(mediaActions.setSelectedMedia(media));
      onClose();
    } catch(error){} finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    setUUID(uuidv4());
    const currentInputRef = inputRef?.current;

    if (currentInputRef) {
      currentInputRef.click();
      currentInputRef.addEventListener('cancel', onClose);
    }

    return () => {
      if (currentInputRef) currentInputRef.removeEventListener('cancel', onClose);
    }
    // eslint-disable-next-line
  }, []);

  const mediasInfo:Record<MediaFormats, any> = {
    [MediaFormats.BottomBanner]: { width: 420, height: 120, hint: 'Ratio 7:2' },
    [MediaFormats.CTA]: { width: 420, height: 350, hint: 'Ratio 6:5. Recommended dimensions W 420px x H 350px' },
    [MediaFormats.Countdown]: { width: 420, height: 236, hint: 'Ratio 16:9' },
    [MediaFormats.Interstitial]: { width: 268, height: 268, hint: 'Ratio 1:1' },
    [MediaFormats.Logo]: { width: 140, height: 60, hint: 'Ratio 7:3' },
    [MediaFormats.Masthead]: { width: 420, height: 168, hint: 'Ratio 5:2' },
    [MediaFormats.Question]: { width: 420, height: 236, hint: 'Ratio 16:9. Recommended dimensions W 420px x H 236px' },
    [MediaFormats.SocialShare]: { width: 300, height: 200, hint: 'Ratio 3:2. Recommended dimensions W 600px x H 400px' },
    [MediaFormats.Wallpaper]: { width: 1920, height: 1080, hint: 'Ratio 16:9. Recommended dimensions W 1920px x H 1080px' },
    [MediaFormats.QRCode]: { width: 180, height: 70, hint: 'Ratio 18:7' },
  }

  return (
    <Dialog size="lg" open={open} onClose={onClose}>
      <DialogTitle title="Upload Image" onClose={onClose} />
      <DialogContent>
        <input
          ref={inputRef}
          className="hidden"
          type="file"
          accept="image/*"
          onChange={handleChange}
        />
        <Select
          id="media-format" name="media-format"
          options={formatOptions}
          value={format}
          onChange={handleFormat}
          label="Format"
        />
        {isLoading ? (
          <div
            className="
              absolute top-0 left-0 w-full h-full
              flex justify-center items-center
              z-10
            "
            style={{ backgroundColor: 'rgba(0,0,0,0.75)' }}
          ><Loader /></div>
        ) : null}
        <div
          className="relative rounded overflow-hidden w-full mb-4"
          style={{
            backgroundImage: `url(${ImageUploadPattern})`,
            boxShadow: 'inset 0 0 16px rgba(0,0,0,0.75)',
            width: `${mediasInfo[format].width}px`,
            height: `${mediasInfo[format].height}px`,
            maxWidth: '100%',
            maxHeight: '100%',
            marginInline: 'auto'
          }}
        >
          <AvatarEditor
            ref={editorRef}
            className="absolute top-0 left-0 rounded"
            style={{
              backgroundImage: `url(${ImageUploadPattern})`,
              width: '100%',
              height: '100%',
            }}
            image={fileDataUrl || ''}
            width={mediasInfo[format].width}
            height={mediasInfo[format].height}
            scale={scale}
            border={0}
            rotate={0}
          />
        </div>
        {!isGif ? (
          <div className="mb-2">
            <input
              className="w-full" id={`image-scale-${uuid}`} name={`image-scale-${uuid}`}
              type="range" value={scale}
              min="0.1" max="2" step="0.1"
              disabled={!fileDataUrl}
              onChange={handleChangeScale}
            />
          </div>
        ) : null}
        {quiz ? (
          <Checkbox
            label="Upload to account media gallery. Will be available for all quizzes"
            id="upload-to-account-media"
            name="upload-to-account-media"
            checked={uploadToAccount}
            onChange={handleChangeUploadToAccount}
          />
        ) : null}
        <span className="block text-gray-400 text-sm font-light leading-5 mt-2">{mediasInfo[format].hint || ''}</span>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
        >{t('common.cancel')}</Button>
        <Button
          variant="contained"
          disabled={!fileDataUrl}
          onClick={handleClickApply}
        >{t('common.apply')}</Button>
      </DialogActions>
    </Dialog>
  )
}

export default UploadImageDialog;

const checkIsGif = (value:string | null):boolean => {
  if ( !value ) return false;
  return value.includes('image/gif');
}

const formatOptions = (Object.keys(MediaFormats) as Array<keyof typeof MediaFormats>)
  .map((key) => ({ value: MediaFormats[key], label: textFromCamelToNormalCase(key) }));
